import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Stack, Columns } from "components/layout/reusableLayouts";
import { FormikProps } from "formik";
import { useMask } from "@react-input/mask";

// Local components
import PlanPickerDrawer from "components/platform/accounts/PlanPickerDrawer";

// Local utilities and validations
import { AccountDetailsDrawerType } from "enums/AccountDetailsDrawerType";
import Role from "enums/Role";
import { useAssignableRoles } from "hooks/useAssignableRoles";
import { useCompanyAppFeatureKeys } from "hooks/useCompanyAppFeatureKeys";
import { CardDesign, FormikAddAccount } from "hooks/useAccountData";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import AccountDetailsDrawer from "components/platform/accounts/AccountDetailsDrawer";
import { useMutation, useQuery } from "@apollo/client";
import { GET_CARD_DESIGNS } from "graphql/card";
import { GET_MY_PLATFORM, GET_COMPANY_PLANS } from "graphql/platform";
import { UPDATE_COMPANY_PLANS } from "graphql/company";
import { toast } from "react-toastify";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { Drawer, Select, TextField } from "@outpave/ds";
import PlatformConfigureAddressDrawer from "../migrate/PlatformConfigureAddressDrawer";
import AddressField from "components/common/address/AddressField";

type PlanSelection = {
  appPlanIds: string[];
  addOnPlanIds: string[];
};

interface EditAccountDrawerProps {
  open: boolean;
  formik: FormikProps<FormikAddAccount>;
  handleClose: (
    refresh?: boolean,
    openAccountDetailsWithId?: string,
    newBusinessname?: string,
  ) => void;
  drawerType: AccountDetailsDrawerType;
  accountId?: string;
  companyId?: string;
  /** Account create/update in flight. Gates the overlay and every close path. */
  savingAccount?: boolean;
}

const EditAccountDrawer: React.FC<EditAccountDrawerProps> = ({
  open,
  handleClose,
  drawerType,
  formik,
  accountId,
  companyId,
  savingAccount = false,
}) => {
  const { t } = useTranslation();

  const [openAddressDrawer, setOpenAddressDrawer] = useState(false);
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [openPlanPicker, setOpenPlanPicker] = useState(false);

  const canManagePlans = useCheckUserRole(Role.PlatformOwner);
  const isNewAccount = drawerType === AccountDetailsDrawerType.NewAccount;

  const { data: cardDesignsData, loading } = useQuery(GET_CARD_DESIGNS, {
    variables: { companyId },
    skip: Boolean(!companyId),
    fetchPolicy: "network-only",
  });

  // Pre-fill from Platform defaults on new account, current Frontegg state on edit
  const { data: platformData } = useQuery(GET_MY_PLATFORM, {
    skip: !open || !isNewAccount || !canManagePlans,
    fetchPolicy: "cache-and-network",
  });
  const { data: companyPlansData, refetch: refetchCompanyPlans } = useQuery(
    GET_COMPANY_PLANS,
    {
      variables: { companyId },
      skip: !open || isNewAccount || !companyId || !canManagePlans,
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (!open || !canManagePlans) return;
    if (isNewAccount && platformData?.myPlatform) {
      formik.setFieldValue(
        "appPlanIds",
        platformData.myPlatform.defaultAppPlanIds || [],
      );
      formik.setFieldValue(
        "addOnPlanIds",
        platformData.myPlatform.defaultAddOnPlanIds || [],
      );
    }
    if (!isNewAccount && companyPlansData?.companyPlans) {
      formik.setFieldValue(
        "appPlanIds",
        companyPlansData.companyPlans.appPlanIds || [],
      );
      formik.setFieldValue(
        "addOnPlanIds",
        companyPlansData.companyPlans.addOnPlanIds || [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNewAccount, platformData, companyPlansData]);

  const [updateCompanyPlans, { loading: savingPlans }] = useMutation(
    UPDATE_COMPANY_PLANS,
    {
      onCompleted: () => {
        toast.success(t("planUpdated"));
        refetchCompanyPlans();
      },
      onError: (err) => {
        toast.error(err?.message || t("failedToUpdatePlans"));
      },
    },
  );

  const handlePlanSave = (next: PlanSelection) => {
    formik.setFieldValue("appPlanIds", next.appPlanIds);
    formik.setFieldValue("addOnPlanIds", next.addOnPlanIds);
    if (!isNewAccount && companyId) {
      updateCompanyPlans({
        variables: {
          companyId,
          input: {
            appPlanIds: next.appPlanIds,
            addOnPlanIds: next.addOnPlanIds,
          },
        },
      });
    }
  };

  const cardDesigns = useMemo(() => {
    return cardDesignsData?.cardDesigns || [];
  }, [cardDesignsData]);

  const targetAppFeatureKeys = useCompanyAppFeatureKeys(companyId, {
    skip: !open,
  });
  const { options: roleOptions, platformAppMissing } = useAssignableRoles(
    [Role.Admin, Role.PlatformAdmin, Role.PlatformOwner],
    formik.values.user?.role as Role,
    companyId,
    targetAppFeatureKeys,
  );

  useEffect(() => {
    if (cardDesigns.length === 1 && !formik.values.account.cardDesign?.id) {
      formik.setFieldValue("account.cardDesign", cardDesigns[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDesigns]);

  // Gates the overlay AND every dismissal path, so the two can't drift.
  // `savingPlans` is the plan mutation this drawer owns; `savingAccount` is the
  // create/update the parent runs. The parent's own LoadingWithoutModal sits at
  // zIndex.drawer + 1, below this drawer's modal, so it never covered it.
  const busy = savingAccount || savingPlans;

  const handleCloseDrawer = useCallback(
    (refresh: boolean = false) => {
      if (busy) return;
      handleClose(refresh);
    },
    [busy, handleClose],
  );

  const handlePrevStep = useCallback(
    (refresh: boolean = false) => {
      if (drawerType === AccountDetailsDrawerType.NewAccount) {
        if (openDetailsDrawer) {
          setOpenDetailsDrawer(false);
        } else {
          handleCloseDrawer(refresh);
        }
        return;
      }
      if (drawerType === AccountDetailsDrawerType.EditExistingAccount) {
        handleCloseDrawer(refresh);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawerType, openDetailsDrawer],
  );

  const handleNextStep = useCallback(() => {
    if (drawerType === AccountDetailsDrawerType.NewAccount) {
      setOpenDetailsDrawer(true);
    } else if (drawerType === AccountDetailsDrawerType.EditExistingAccount) {
      formik.submitForm();
      handleCloseDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerType]);

  const handleBlurEmail = (e: { target: { name: string } }) => {
    formik.handleBlur(e);
  };

  useEffect(() => {
    setTimeout(() => {
      formik.validateForm();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  return (
    <>
      <Drawer
        open={open}
        loading={busy}
        title={
          drawerType === AccountDetailsDrawerType.NewAccount
            ? t("Add Account")
            : t("Edit Account")
        }
        footer={
          <>
            <Button
              variant="primary-outlined"
              disabled={busy}
              onClick={() => {
                handlePrevStep(false);
              }}
            >
              {t("Exit")}
            </Button>

            {canManagePlans && (
              <Button
                variant="primary-outlined"
                onClick={() => setOpenPlanPicker(true)}
                disabled={busy}
              >
                {t("Manage Plans")}
              </Button>
            )}

            <Button
              variant="primary-filled"
              onClick={() => {
                handleNextStep();
              }}
              disabled={
                busy ||
                Object.keys(formik.errors).length > 0 ||
                !formik.isValid ||
                !formik.dirty
              }
            >
              {drawerType === AccountDetailsDrawerType.NewAccount
                ? t("Next")
                : t("Save")}
            </Button>
          </>
        }
        onClose={() => {
          handleCloseDrawer();
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          {drawerType === AccountDetailsDrawerType.NewAccount ? (
            <Stack>
              <TextField
                label={t("Account Name")}
                fullWidth
                placeholder={t("Account Name")}
                name="account.businessLegalName"
                value={formik.values.account.businessLegalName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />

              <Columns count={2}>
                <TextField
                  label={t("First Name")}
                  required
                  fullWidth
                  placeholder={t("First Name")}
                  name="user.firstName"
                  value={formik.values.user.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.firstName
                      ? formik.errors.user?.firstName
                      : undefined
                  }
                />
                <TextField
                  label={t("Last Name")}
                  required
                  fullWidth
                  placeholder={t("Last Name")}
                  name="user.lastName"
                  value={formik.values.user.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.lastName
                      ? formik.errors.user?.lastName
                      : undefined
                  }
                />

                <TextField
                  label={t("Phone")}
                  required
                  fullWidth
                  ref={phoneMaskRef}
                  name="user.phone"
                  value={formik.values.user?.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.phone
                      ? formik.errors.user?.phone
                      : undefined
                  }
                />

                <Select
                  label={t("Role")}
                  required
                  fullWidth
                  options={roleOptions.map(({ role, disabled }) => ({
                    id: role,
                    label: t(role),
                    disabled,
                  }))}
                  value={formik.values.user?.role ?? null}
                  onChange={(id) => formik.setFieldValue("user.role", id)}
                  helperText={
                    platformAppMissing
                      ? t("PlatformRolesNeedPlatformApp")
                      : undefined
                  }
                  errorText={
                    formik.touched.user?.role
                      ? (formik.errors.user?.role as string | undefined)
                      : undefined
                  }
                />
              </Columns>

              <TextField
                label={t("Email")}
                required
                fullWidth
                name="user.email"
                value={formik.values.user.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorText={
                  formik.touched.user?.email
                    ? formik.errors.user?.email
                    : undefined
                }
              />

              <AddressField
                label={t("Address")}
                required
                placeholder={t("Address")}
                name="user.address"
                address={formik.values.user.address}
                onOpen={() => setOpenAddressDrawer(true)}
                errorText={
                  formik.touched.user?.address
                    ? formik.errors.user?.address?.addressName
                    : undefined
                }
              />
            </Stack>
          ) : (
            <Stack>
              <TextField
                label={t("Legal Name")}
                fullWidth
                placeholder={t("Legal Name")}
                name="account.businessLegalName"
                value={formik.values.account.businessLegalName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Doing Business As (DBA)")}
                fullWidth
                placeholder={t("Doing Business As (DBA)")}
                name="account.businessDBA"
                value={formik.values.account.businessDBA}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Business Card Name")}
                fullWidth
                placeholder={t("Business Card Name")}
                name="account.businessCardName"
                value={formik.values.account.businessCardName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Description")}
                fullWidth
                placeholder={t("Description")}
                name="account.description"
                value={formik.values.account.description}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Email")}
                fullWidth
                placeholder={t("Email")}
                name="account.businessEmail"
                value={formik.values.account.businessEmail}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Phone")}
                fullWidth
                placeholder={t("Phone")}
                name="account.phoneNumber"
                value={formik.values.account.phoneNumber}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <Select
                label={t("Card Design")}
                fullWidth
                options={cardDesigns.map((item: CardDesign) => ({
                  id: item.id,
                  label: item.name,
                }))}
                value={formik.values.account.cardDesign?.id ?? null}
                onChange={(id) => {
                  const selectedDesign = cardDesigns.find(
                    (item: CardDesign) => item.id === id,
                  );
                  formik.setFieldValue("account.cardDesign", selectedDesign);
                }}
              />
            </Stack>
          )}
        </form>
      </Drawer>

      {canManagePlans && (
        <PlanPickerDrawer
          open={openPlanPicker}
          onClose={() => setOpenPlanPicker(false)}
          appPlanIds={formik.values.appPlanIds || []}
          addOnPlanIds={formik.values.addOnPlanIds || []}
          onSave={handlePlanSave}
          companyName={formik.values.account.businessLegalName}
        />
      )}

      {openDetailsDrawer && (
        <AccountDetailsDrawer
          formik={formik}
          open={open}
          handleCloseDrawer={handleClose}
          onBack={() => setOpenDetailsDrawer(false)}
          drawerType={AccountDetailsDrawerType.NewAccount}
        />
      )}

      {openAddressDrawer && (
        <PlatformConfigureAddressDrawer
          open={openAddressDrawer}
          drawerTitle={[t("User Address")]}
          initialValues={formik.values.user.address}
          onClose={() => setOpenAddressDrawer(false)}
          onSuccess={(address: any) => {
            formik.setFieldValue("user.address", address);
            setOpenAddressDrawer(false);
          }}
          header={t("User Address")}
          subheader=""
          readOnly={false}
          customAddressName={false}
        />
      )}
      <LoadingWithoutModal open={loading} />
    </>
  );
};

export default EditAccountDrawer;
